import { Model } from 'infra/model'
import { Id } from 'types/definitions'
import User from 'iam/models/User'
import Workspace from 'iam/models/Workspace'
import { PrimeWorkspace } from 'iam/WorkspaceStore'
import { AdminTransactionResource } from './dtos/admin-transactions'
import {
  UserTransactionReason,
  parseUserTransactionReason,
} from '../UserTransactionReason'
import { TransactionType, parseTransactionType } from '../TransactionType'
import { PrimeUser } from 'iam/UserStore'
import {
  parseTransactionSourceType,
  TransactionSourceType,
} from '../TransactionSourceType'
import { TransactionStatus } from '../TransactionStatus'
import { observable, makeObservable } from 'mobx'
import { DateZero, maybeParseDate } from 'utils/date-util'
import { TransactionFlag, parseTransactionFlag } from '../TransactionFlag'
import {
  TransactionFailureReason,
  parseTransactionFailureReason,
} from '../TransactionFailureReason'

/**
 * A payout transaction's details, from the admin perspective.
 */
export class AdminTransaction extends Model<
  AdminTransactionResource,
  AdminTransaction
> {
  id: Id
  shortId = ''
  amount = 0
  createdAt = DateZero()
  type = TransactionType.Payment
  recipient = User.none
  workspace = Workspace.none
  source = TransactionSourceType.Unknown

  reason: UserTransactionReason | null = UserTransactionReason.Unknown
  memo: string | null = null

  jobId: string | null = null

  flags: TransactionFlag[] = []

  @observable status = TransactionStatus.Unknown
  @observable iteration = 1
  @observable canceledAt: Date | null = null
  @observable approvedAt: Date | null = null
  @observable completedAt: Date | null = null

  @observable approvedByUserId: Id | null = null
  @observable payoutId: Id | null = null

  @observable stripeTransferId: string | null = null
  @observable stripeAccountId: string | null = null

  @observable lastFailureReason: TransactionFailureReason | null = null
  @observable lastFailureDescription: string | null = null

  constructor(
    private primeUser: PrimeUser,
    private primeWorkspace: PrimeWorkspace,
  ) {
    super()
    makeObservable(this)
  }

  parse(attrs: Partial<AdminTransactionResource>): Partial<AdminTransaction> {
    return {
      id: attrs.id,
      shortId: attrs.short_id,
      amount: attrs.amount,
      iteration: attrs.iteration,
      createdAt: maybeParseDate(attrs.date_created),
      canceledAt: maybeParseDate(attrs.date_canceled),
      approvedAt: maybeParseDate(attrs.date_approved),
      completedAt: maybeParseDate(attrs.date_completed),
      approvedByUserId: attrs.approved_by_user_id,
      payoutId: attrs.payout_id,
      stripeAccountId: attrs.stripe_account_id,
      stripeTransferId: attrs.stripe_transfer_id,
      recipient: attrs.recipient_user_id
        ? this.primeUser({ public_id: attrs.recipient_user_id })
        : undefined,
      workspace: attrs.workspace_id
        ? this.primeWorkspace({ id: attrs.workspace_id })
        : undefined,
      type: attrs.type && parseTransactionType(attrs.type),
      source: attrs.source && parseTransactionSourceType(attrs.source),
      reason: attrs.reason && parseUserTransactionReason(attrs.reason),
      memo: attrs.memo,
      jobId: attrs.job_id,
      flags: attrs.flags?.map(parseTransactionFlag),
      lastFailureReason:
        attrs.last_failure_reason &&
        parseTransactionFailureReason(attrs.last_failure_reason),
      lastFailureDescription: attrs.last_failure_description,
    }
  }
}
